import Vue from 'vue';
import { pick } from 'lodash-es';

import Shortener from '../services/shortener';
import { getTheme } from '../services/themes';

import getDefaultFields from './graphics-fields';
import { setFields } from './graphics-utils';

import toast from '../components/toast/toast';

/**
 * Shorten all URLs associated with QR codes inside the timeline panel.
 * @param {Object} opts.post - Timeline item.
 * @param {Object} opts.panels - Map containing all panel data by ID.
 * @param {Object} opts.production - Current production.
 * @param {Array<String>} [opts.timelineIds] - List of timeline items to process.
 *   If not provided, will process the whole timeline.
 */
export const shortenPanelUrls = async (opts = {}) => {
  const {
    post,
    panels,
    timelineIds,
    production,
  } = opts;

  // If provided post isn't even a panel, then do nothing.
  if (post?.layout !== 'panel') { return; }

  const { owner } = production;

  const panel = panels[post._id];
  if (!panel) { return; }

  const fields = { ...panel.settings, ...(post.fields || {}) };

  const { showQrCode, qrCodeUrl, qrCodeShortUrl, pollUrl } = fields;

  const qrCodeSourceUrl = pollUrl || qrCodeUrl;

  const shouldShorten = !!(
    // Shorten if timelineIds provided and there's a URL to shorten.
    // Will override any existing short URL.
    (timelineIds?.length && showQrCode && qrCodeSourceUrl)

    // Or shorten if we don't already have the short URL.
    || (showQrCode && qrCodeSourceUrl && !qrCodeShortUrl)
  );

  if (!shouldShorten) {
    // Nothing to shorten and/or URL is already shortened
    return;
  }

  const resp = await Shortener.getShortURL(qrCodeSourceUrl, {
    user: owner,
    production: {
      id: production.id,
      title: production.title,
    },
    item: post,
    element: post,
  }).catch((err) => {
    if (err?.type !== 'FetchError') {
      console.error('Failed to shorten panel URL:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to generate short links for QR Code.',
      });
    }
  });

  if (!resp) { return; }

  const { shortUrl } = resp.data;

  // Save fields to timeline item
  Vue.set(post, 'fields', post.fields || { visible: true });
  Vue.set(post.fields, 'qrCodeUrl', qrCodeSourceUrl);
  Vue.set(post.fields, 'qrCodeShortUrl', shortUrl);
};

/**
 * Shorten all QR code URLs found within graphic template.
 * @param {Object} opts.post - Timeline item.
 * @param {Object} opts.production - Current production.
 * @param {Array<String>} [opts.timelineIds] - List of timeline items to process.
 *   If not provided, will process the whole timeline.
 */
export const shortenGraphicUrls = async (opts = {}) => {
  const {
    post,
    production,
    timelineIds,
    templates,
    themes = {},
  } = opts;

  const { owner } = production;

  if (post?.layout !== 'graphic') { return; }

  if (timelineIds && !timelineIds.includes(post.timeline_id)) {
    // Don't regenerate short URL unless directly specified
    // (when timelineIds exists, and contains the post)
    return;
  }

  const template = templates[post.template_id];
  if (!template) { return; }

  const processElements = (parentEl) => {
    const { elements } = parentEl;

    if (!elements?.length) {
      return Promise.resolve();
    }

    // Loop over individual elements inside the graphic template
    return Promise.all(
      elements.map(async (element) => {
        // Handle slideshow separately because it's dumb
        if (element.type === 'slideshow') {
          let fields = post.fields?.[element.id];

          // Step 0: If there's no fields, we need those for slideshows or we can't set fields for `entries`.
          if (!fields) {
            // This will mutate `post.fields` for us...
            setFields(post, [element]);
            fields = post.fields?.[element.id];
          }

          // Step 1: Loop over `fields.entries` and treat them like elements in need of shortening.
          fields?.entries?.forEach((slide, slideIdx) => {
            slide.fields.forEach(async (layer, idx) => {
              if (layer.type === 'qrcode' && layer.permalink) {
                // Generate a short URL and save to fields on timeline post
                const resp = await Shortener.getShortURL(layer.permalink, {
                  user: owner,
                  production: {
                    id: production.id,
                    title: production.title,
                  },
                  item: post,
                  element,
                });

                const { shortUrl } = resp.data;

                Vue.set(fields.entries[slideIdx].fields, idx, {
                  ...layer,
                  shortLink: shortUrl,
                });

                // I guess copy to the root level post too? mannnn idk
                Vue.set(post.fields, layer.id, {
                  ...layer,
                  shortLink: shortUrl,
                });
              }
            });
          });
        }

        if (element.elements?.length && element.type !== 'slideshow') {
          await processElements(element);
        }

        if (element.type === 'qrcode') {
          const fields = post.fields?.[element.id] || {};

          if (!element.permalink || element.data?.provider) {
            // Nothing to shorten
            return;
          }

          if (!timelineIds && fields.shortLink) {
            // URL is already shortened and SHOULD be correct, so skip over this one
            // But if `timelineIds` are provided, we'll regenerate no matter what.
            return;
          }

          // Fallback to `element.permalink` if field not already set.
          fields.permalink = fields.permalink || element.permalink;

          // Generate a short URL and save to fields on timeline post
          const resp = await Shortener.getShortURL(element.permalink, {
            user: owner,
            production: {
              id: production.id,
              title: production.title,
            },
            item: post,
            element,
          });

          const { shortUrl } = resp.data;
          fields.shortLink = shortUrl;

          // Save fields to timeline item
          post.fields = post.fields || {};

          Vue.set(post.fields, element.id, {
            // Defaults
            ...getDefaultFields('qrcode'),
            ...pick(element, Object.keys(getDefaultFields('qrcode'))),

            // Existing Fields
            ...post.fields[element.id],

            // Permalink/Short URL changes
            ...fields,
          });
        }

        // Handle social-display frames with QR codes
        if (element.type === 'social-display' || element.isSocialDisplay === true) {
          const fields = post.fields?.[element.id] || {};

          // Determine if we need to check theme defaults
          const needsThemeDefaults = !fields.showQrCode && !fields.permalink;

          let shouldShorten = false;
          let urlToShorten = null;

          if (needsThemeDefaults && element.displayId) {
            // Check theme settings for initial QR code configuration
            let theme = themes[element.displayId];

            // If theme not in lookup table, try to fetch it
            if (!theme) {
              try {
                theme = await getTheme(element.displayId);
                // Cache it in the themes object for future use
                if (theme) {
                  themes[element.displayId] = theme;
                }
              } catch (err) {
                console.warn('Failed to fetch theme for social display:', element.displayId, err);
              }
            }

            // Check if theme has QR code enabled by default
            if (theme?.settings?.showQrCode && theme.settings.permalink) {
              // Initialize fields from theme defaults
              Vue.set(post, 'fields', post.fields || {});
              Vue.set(post.fields, element.id, {
                ...post.fields[element.id],
                showQrCode: theme.settings.showQrCode,
                permalink: theme.settings.permalink,
              });

              shouldShorten = true;
              urlToShorten = theme.settings.permalink;
            }
          } else if (fields.showQrCode && fields.permalink) {
            // User has explicitly set QR code settings
            shouldShorten = true;
            urlToShorten = fields.permalink;
          }

          // Skip if no URL to shorten
          if (!shouldShorten || !urlToShorten) {
            return;
          }

          // Skip if already shortened (unless forcing regeneration)
          if (!timelineIds && fields.shortLink) {
            return;
          }

          // Generate a short URL for the social display QR code
          const resp = await Shortener.getShortURL(urlToShorten, {
            user: owner,
            production: {
              id: production.id,
              title: production.title,
            },
            item: post,
            element,
          }).catch((err) => {
            if (err?.type !== 'FetchError') {
              console.error('Failed to shorten social display URL:', err);

              toast.makeToast({
                heading: 'Error',
                icon: 'error',
                loaderBg: 'red',
                text: 'Failed to generate short links for Social Display QR Code.',
              });
            }
          });

          if (!resp) { return; }

          const { shortUrl } = resp.data;

          // Save fields to timeline item
          post.fields = post.fields || {};
          Vue.set(post.fields, element.id, {
            ...post.fields[element.id],
            shortLink: shortUrl,
          });
        }
      }),
    );
  };

  await processElements(template);
};

/**
 * Shorten all URLs associated with QR codes inside the timeline theme.
 * @param {Object} opts.post - Timeline item.
 * @param {Object} opts.themes - Map containing all theme data by ID.
 * @param {Object} opts.production - Current production.
 * @param {Array<String>} [opts.timelineIds] - List of timeline items to process.
 *   If not provided, will process the whole timeline.
 */
export const shortenThemeUrls = async (opts = {}) => {
  const {
    post,
    themes,
    timelineIds,
    production,
  } = opts;

  // If provided post isn't even a panel, then do nothing.
  if (!['grid', 'waterfall', 'carousel', 'isolated'].includes(post?.layout)) { return; }

  const { owner } = production;

  const theme = themes[post._id];
  if (!theme) { return; }

  const fields = { ...theme.settings, ...(post.fields || {}) };

  const { showQrCode, permalink, shortLink } = fields;

  const shouldShorten = !!(
    // Shorten if timelineIds provided and there's a URL to shorten.
    // Will override any existing short URL.
    (timelineIds?.length && showQrCode && permalink)

    // Or shorten if we don't already have the short URL.
    || (showQrCode && permalink && !shortLink)
  );

  if (!shouldShorten) {
    // Nothing to shorten and/or URL is already shortened
    return;
  }

  const resp = await Shortener.getShortURL(permalink, {
    user: owner,
    production: {
      id: production.id,
      title: production.title,
    },
    item: post,
    element: post,
  });

  const { shortUrl } = resp.data;
  fields.shortLink = shortUrl;

  // Save fields to timeline item
  Vue.set(post, 'fields', post.fields || { visible: true });
  Vue.set(post.fields, 'shortLink', shortUrl);
  Vue.set(post.fields, 'permalink', permalink);
};
